import React, { useEffect, useMemo, useRef, useState } from 'react';
import { gql, useMutation, useQuery } from '@apollo/client';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from '@/components/ui/toast';

const RECAPTCHA_SITE_KEY = process.env.REACT_APP_EFAX_RECAPTCHA_SITE_KEY || '6LcuVigUAAAAAEU0s6uw-d-t_z3kqus4F7GYH3Me';
const RECAPTCHA_SCRIPT_ID = 'efax-recaptcha-script';
const RECAPTCHA_CONTAINER_ID = 'efax-recaptcha-container';
const LOCAL_CAPTCHA_BYPASS = process.env.NODE_ENV !== 'production';
const FALLBACK_PROGRAM_OPTIONS = ['MIST', 'FIST', 'ODRH Hub 2'];

const GET_EFAX_PROGRAM_OPTIONS = gql`
  query EfaxProgramOptions {
    efaxProgramOptions
  }
`;

const GET_EFAX_LETTER_TYPE_OPTIONS = gql`
  query EfaxLetterTypeOptions($program: String) {
    efaxLetterTypeOptions(program: $program)
  }
`;

const SUBMIT_EFAX_FORM = gql`
  mutation SubmitEfaxForm($input: EfaxSubmissionInput!) {
    submitEfaxForm(input: $input) {
      message
      responseCode
      responseID
      extra
    }
  }
`;

type EfaxFormState = {
  senderemail: string;
  program: string;
  firstname: string;
  lastname: string;
  casenumber: string;
  lettertype: string;
};

type EfaxProgramOptionsData = {
  efaxProgramOptions: string[];
};

type EfaxLetterTypeOptionsData = {
  efaxLetterTypeOptions: string[];
};

declare global {
  interface Window {
    grecaptcha?: {
      render: (container: string | HTMLElement, parameters: Record<string, any>) => number;
      reset: (widgetId?: number) => void;
    };
  }
}

const readFileAsBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (event: ProgressEvent<FileReader>) => {
      const result = `${event.target?.result || ''}`;
      const [, base64] = result.split(',');
      if (!base64) {
        reject(new Error('Unable to parse file data'));
        return;
      }

      resolve(base64);
    };

    reader.onerror = () => {
      reject(new Error('Unable to read file'));
    };

    reader.readAsDataURL(file);
  });
};

export const EfaxFormPage: React.FC = () => {
  const [formState, setFormState] = useState<EfaxFormState>({
    senderemail: '',
    program: '',
    firstname: '',
    lastname: '',
    casenumber: '',
    lettertype: '',
  });
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [captchaToken, setCaptchaToken] = useState('');
  const [captchaLoadError, setCaptchaLoadError] = useState('');
  const [captchaReady, setCaptchaReady] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [fileInputKey, setFileInputKey] = useState(0);

  const widgetIdRef = useRef<number | null>(null);

  const { data: programData } = useQuery<EfaxProgramOptionsData>(GET_EFAX_PROGRAM_OPTIONS, {
    fetchPolicy: 'cache-first',
  });
  const { data: letterTypeData, loading: letterTypeLoading } = useQuery<EfaxLetterTypeOptionsData>(
    GET_EFAX_LETTER_TYPE_OPTIONS,
    {
      variables: {
        program: formState.program || null,
      },
      fetchPolicy: 'cache-and-network',
    },
  );
  const [submitEfaxForm] = useMutation(SUBMIT_EFAX_FORM);

  const programOptions = useMemo(() => {
    const values = Array.isArray(programData?.efaxProgramOptions)
      ? (programData?.efaxProgramOptions || [])
      : [];
    return values.length > 0 ? values : FALLBACK_PROGRAM_OPTIONS;
  }, [programData]);

  const letterTypeOptions = useMemo(() => {
    const values = Array.isArray(letterTypeData?.efaxLetterTypeOptions)
      ? (letterTypeData?.efaxLetterTypeOptions || [])
      : [];

    return values.filter((value) => `${value || ''}`.trim().length > 0);
  }, [letterTypeData]);

  useEffect(() => {
    if (LOCAL_CAPTCHA_BYPASS) {
      setCaptchaToken('local-bypass');
      setCaptchaReady(true);
      setCaptchaLoadError('');
      return;
    }

    if (!RECAPTCHA_SITE_KEY) {
      setCaptchaLoadError('Captcha is not configured.');
      return;
    }

    const renderCaptcha = () => {
      if (!window.grecaptcha || widgetIdRef.current !== null) {
        return;
      }

      const container = document.getElementById(RECAPTCHA_CONTAINER_ID);
      if (!container) {
        return;
      }

      try {
        widgetIdRef.current = window.grecaptcha.render(container, {
          sitekey: RECAPTCHA_SITE_KEY,
          callback: (token: string) => {
            setCaptchaToken(token);
          },
          'expired-callback': () => {
            setCaptchaToken('');
          },
          'error-callback': () => {
            setCaptchaToken('');
          },
        });

        setCaptchaReady(true);
        setCaptchaLoadError('');
      } catch (error) {
        setCaptchaLoadError('Unable to initialize captcha.');
      }
    };

    if (window.grecaptcha) {
      renderCaptcha();
      return;
    }

    const existingScript = document.getElementById(RECAPTCHA_SCRIPT_ID) as HTMLScriptElement | null;
    if (existingScript) {
      existingScript.addEventListener('load', renderCaptcha, { once: true });
      return;
    }

    const script = document.createElement('script');
    script.id = RECAPTCHA_SCRIPT_ID;
    script.src = 'https://www.google.com/recaptcha/api.js?render=explicit';
    script.async = true;
    script.defer = true;
    script.onload = () => {
      renderCaptcha();
    };
    script.onerror = () => {
      setCaptchaLoadError('Unable to load captcha.');
    };

    document.body.appendChild(script);
  }, []);

  const setField = (field: keyof EfaxFormState, value: string) => {
    setFormState((current) => ({
      ...current,
      [field]: value,
    }));
  };

  const resetForm = () => {
    setFormState({
      senderemail: '',
      program: '',
      firstname: '',
      lastname: '',
      casenumber: '',
      lettertype: '',
    });
    setUploadedFile(null);
    setFileInputKey((value) => value + 1);
    setCaptchaToken(LOCAL_CAPTCHA_BYPASS ? 'local-bypass' : '');

    if (window.grecaptcha && widgetIdRef.current !== null) {
      window.grecaptcha.reset(widgetIdRef.current);
    }
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (submitting) {
      return;
    }

    if (!formState.senderemail || !formState.program || !formState.firstname || !formState.lastname || !formState.casenumber || !formState.lettertype) {
      toast.error('Complete all required fields before submitting.');
      return;
    }

    if (!uploadedFile) {
      toast.error('Attach a PDF file before submitting.');
      return;
    }

    if (uploadedFile.type !== 'application/pdf') {
      toast.error('Only PDF files are allowed.');
      return;
    }

    if (!captchaToken) {
      toast.error('Complete the captcha before submitting.');
      return;
    }

    setSubmitting(true);
    try {
      const fileBase64 = await readFileAsBase64(uploadedFile);
      const response = await submitEfaxForm({
        variables: {
          input: {
            senderemail: formState.senderemail.trim(),
            program: formState.program,
            firstname: formState.firstname.trim(),
            lastname: formState.lastname.trim(),
            casenumber: formState.casenumber.trim(),
            lettertype: formState.lettertype,
            captchaToken,
            fileName: uploadedFile.name,
            fileMimeType: uploadedFile.type || 'application/pdf',
            fileBase64,
          },
        },
      });

      const payload = response?.data?.submitEfaxForm;
      if (Number(payload?.responseCode) !== 200) {
        throw new Error(payload?.message || 'Unable to submit fax form');
      }

      toast.success('Fax submitted successfully.');
      resetForm();
    } catch (error: any) {
      toast.error(error?.message || 'Unable to submit fax form');
      if (window.grecaptcha && widgetIdRef.current !== null) {
        window.grecaptcha.reset(widgetIdRef.current);
      }
      setCaptchaToken(LOCAL_CAPTCHA_BYPASS ? 'local-bypass' : '');
    }

    setSubmitting(false);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="mx-auto max-w-3xl space-y-4 px-4 py-8">
        <Card>
          <CardHeader>
            <CardTitle>Office of Diversion &amp; Reentry Electronic Fax</CardTitle>
          </CardHeader>
          <CardContent>
            <form className="space-y-5" onSubmit={handleSubmit}>
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-1.5">
                  <Label htmlFor="efax-sender-email">Your email</Label>
                  <Input
                    id="efax-sender-email"
                    type="email"
                    value={formState.senderemail}
                    onChange={(event) => setField('senderemail', event.target.value)}
                    required
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="efax-program">Program</Label>
                  <select
                    id="efax-program"
                    className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                    value={formState.program}
                    onChange={(event) => {
                      setField('program', event.target.value);
                      setField('lettertype', '');
                    }}
                    required
                  >
                    <option value="">Select program</option>
                    {programOptions.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-1.5">
                  <Label htmlFor="efax-firstname">Client first name</Label>
                  <Input
                    id="efax-firstname"
                    value={formState.firstname}
                    onChange={(event) => setField('firstname', event.target.value)}
                    required
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="efax-lastname">Client last name</Label>
                  <Input
                    id="efax-lastname"
                    value={formState.lastname}
                    onChange={(event) => setField('lastname', event.target.value)}
                    required
                  />
                </div>
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-1.5">
                  <Label htmlFor="efax-casenumber">Case number(s)</Label>
                  <Input
                    id="efax-casenumber"
                    value={formState.casenumber}
                    onChange={(event) => setField('casenumber', event.target.value)}
                    required
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="efax-lettertype">Type of report</Label>
                  <select
                    id="efax-lettertype"
                    className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                    value={formState.lettertype}
                    onChange={(event) => setField('lettertype', event.target.value)}
                    required
                    disabled={!formState.program || letterTypeLoading}
                  >
                    <option value="">{letterTypeLoading ? 'Loading report types...' : 'Select report type'}</option>
                    {letterTypeOptions.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="efax-file">Upload PDF</Label>
                <Input
                  key={fileInputKey}
                  id="efax-file"
                  type="file"
                  accept="application/pdf"
                  onChange={(event) => {
                    const file = event.target.files?.[0] || null;
                    setUploadedFile(file);
                  }}
                  required
                />
              </div>

              <div className="space-y-1.5">
                <Label>Captcha</Label>
                <div className="rounded-md border border-gray-200 bg-white p-3">
                  {LOCAL_CAPTCHA_BYPASS
                    ? <p className="text-sm text-gray-600">Captcha bypassed in local development.</p>
                    : <div id={RECAPTCHA_CONTAINER_ID} />}
                </div>
                {!captchaReady && !captchaLoadError && (
                  <p className="text-sm text-gray-600">Loading captcha...</p>
                )}
                {captchaLoadError && (
                  <p className="text-sm text-red-600">{captchaLoadError}</p>
                )}
              </div>

              <div className="flex items-center gap-2">
                <Button type="submit" disabled={submitting || !captchaReady}>
                  {submitting ? 'Submitting...' : 'Submit Fax'}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={resetForm}
                  disabled={submitting}
                >
                  Reset
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default EfaxFormPage;
